<template>
    <div class="min-h-screen">
        <div class="mx-auto py-3 sm:py-4">
            <!-- Header -->
            <div class="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 class="mb-1 text-3xl font-black text-foxy-text sm:text-4xl">Добірки</h1>
                    <p class="foxy-muted text-base">Знайдіть цікаві добірки книг або створіть
                        власну</p>
                </div>
                <button v-if="isAuthenticated" @click="goToCreate"
                    class="foxy-btn-primary self-start !px-6 !py-2 shadow-lg sm:self-auto">
                    <span class="flex items-center space-x-2">
                        <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        <span>Додати</span>
                    </span>
                </button>
            </div>
            <div class="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div class="flex flex-wrap items-center gap-2">
                    <button type="button" class="rounded-xl px-4 py-2 text-sm font-semibold transition-colors sm:text-base" :class="activeMode === 'all' ? 'bg-foxy-action text-white' : 'bg-foxy-surface text-foxy-text-secondary hover:bg-foxy-elevated hover:text-foxy-text'" @click="switchMode('all')">Усі</button>
                    <template v-if="isAuthenticated">
                        <button type="button" class="rounded-xl px-4 py-2 text-sm font-semibold transition-colors sm:text-base" :class="activeMode === 'my' ? 'bg-foxy-action text-white' : 'bg-foxy-surface text-foxy-text-secondary hover:bg-foxy-elevated hover:text-foxy-text'" @click="switchMode('my')">Мої</button>
                        <button type="button" class="rounded-xl px-4 py-2 text-sm font-semibold transition-colors sm:text-base" :class="activeMode === 'saved' ? 'bg-foxy-action text-white' : 'bg-foxy-surface text-foxy-text-secondary hover:bg-foxy-elevated hover:text-foxy-text'" @click="switchMode('saved')">Збережені</button>
                    </template>
                </div>
                <div class="flex w-full min-w-0 flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
                    <div class="relative w-full min-w-0 sm:w-72">
                    <label class="sr-only" for="library-search">Пошук добірок</label>
                    <svg class="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-foxy-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m21 21-4.35-4.35m2.35-5.65a8 8 0 1 1-16 0 8 8 0 0 1 16 0Z" />
                    </svg>
                    <input id="library-search" v-model="filters.search" type="search" @input="handleSearchInput"
                        class="foxy-input !py-2.5 !pl-10 !pr-10 !text-sm"
                        placeholder="Пошук добірок…">
                    <button v-if="filters.search" type="button" class="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-lg p-2 text-foxy-text-secondary hover:bg-foxy-elevated hover:text-foxy-text" aria-label="Очистити пошук" @click="clearSearch">
                        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18 18 6M6 6l12 12" />
                        </svg>
                    </button>
                    </div>
                    <div class="flex min-w-0 items-center gap-2">
                        <label class="sr-only" for="library-sort">Сортування</label>
                        <select id="library-sort" v-model="filters.sort" @change="applyFilters"
                            class="foxy-select min-w-0 flex-1 !px-3 !py-2.5 !text-sm sm:w-56 sm:flex-none">
                            <option value="popular">Популярні</option>
                            <option value="newest">Найновіші</option>
                            <option value="oldest">Найстаріші</option>
                            <option value="name_asc">За назвою А–Я</option>
                            <option value="name_desc">За назвою Я–А</option>
                            <option value="books_count">За кількістю книг</option>
                        </select>
                        <button v-if="hasNonDefaultFilters" type="button" class="shrink-0 rounded-lg px-2.5 py-2 text-sm font-semibold text-foxy-text-secondary hover:bg-foxy-elevated hover:text-foxy-action" @click="resetFilters">
                            Скинути
                        </button>
                    </div>
                </div>
            </div>
            <div v-if="activeMode === 'my'" class="mb-4 flex flex-wrap items-center gap-1.5">
                <span class="foxy-muted mr-1 text-xs font-semibold uppercase tracking-wide">Видимість</span>
                <button v-for="option in visibilityOptions" :key="option.value" type="button" class="rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors" :class="visibility === option.value ? 'bg-foxy-action text-white' : 'bg-foxy-elevated text-foxy-text-secondary hover:bg-foxy-surface hover:text-foxy-text'" @click="setVisibility(option.value)">{{ option.label }}</button>
            </div>

            <div>
                    <div v-if="loading"
                        class="foxy-muted py-16 flex items-center justify-center">
                        <svg class="animate-spin h-8 w-8 mr-3 text-foxy-action" xmlns="http://www.w3.org/2000/svg"
                            fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4">
                            </circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                        </svg>
                        Завантаження добірок...
                    </div>

                    <template v-else>
                        <div v-if="hasLibraries" class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                            <library-collection v-for="library in libraries" :key="library.id" :library="library"
                                :is-authenticated="isAuthenticated" :is-liked="library.is_liked"
                                :is-saved="library.is_saved" :can-save="library.can_save"
                                :likes-count="library.likes_count" :is-owner="library.is_owner"
                                @notification="showNotification" @liked="handleLiked"
                                @saved="handleSaved" @manage="openManager" @delete="deleteLibrary"></library-collection>
                        </div>
                        <div v-else class="text-center py-12">
                            <div class="text-foxy-text-secondary text-6xl mb-4">
                                <svg class="w-24 h-24 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd"
                                        d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                                        clip-rule="evenodd" />
                                </svg>
                            </div>
                            <h3 class="text-xl font-bold text-foxy-text mb-2">{{ emptyTitle }}</h3>
                            <p class="foxy-muted mb-6">{{ emptyMessage }}</p>
                            <button v-if="isAuthenticated && activeMode === 'my'" @click="goToCreate"
                                class="foxy-btn-primary !px-6 !py-3">
                                Створити добірку
                            </button>
                        </div>
                    </template>

                    <div v-if="!loading && hasLibraries && meta && meta.last_page > 1"
                        class="mt-8 flex items-center justify-center space-x-2">
                        <button
                            class="px-3 py-2 text-sm rounded-lg bg-foxy-surface text-foxy-text-secondary hover:bg-foxy-elevated hover:text-foxy-text disabled:opacity-50"
                            :disabled="meta.current_page <= 1" @click="goToPage(meta.current_page - 1)">
                            Попередня
                        </button>
                        <button v-for="page in pages" :key="page" class="px-3 py-2 text-sm rounded-lg"
                            :class="page === meta.current_page ? 'bg-foxy-action text-white' : 'bg-foxy-surface text-foxy-text-secondary hover:bg-foxy-elevated hover:text-foxy-text'"
                            @click="goToPage(page)">
                            {{ page }}
                        </button>
                        <button
                            class="px-3 py-2 text-sm rounded-lg bg-foxy-surface text-foxy-text-secondary hover:bg-foxy-elevated hover:text-foxy-text disabled:opacity-50"
                            :disabled="meta.current_page >= meta.last_page" @click="goToPage(meta.current_page + 1)">
                            Наступна
                        </button>
                    </div>
            </div>
            <library-books-manager v-if="managedLibrary" :library="managedLibrary" @close="managedLibrary = null" @notification="showNotification" @books-changed="handleBooksChanged"></library-books-manager>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import LibraryCollection from './LibraryCollection.vue';
import LibraryBooksManager from './LibraryBooksManager.vue';

export default {
    name: 'LibrariesExplorer',
    components: { LibraryCollection, LibraryBooksManager },
    props: {
        initialLibraries: {
            type: Array,
            default: () => [],
        },
        initialMeta: {
            type: Object,
            default: () => ({ current_page: 1, last_page: 1, per_page: 12, total: 0 }),
        },
        initialFilters: {
            type: Object,
            default: () => ({}),
        },
        initialLinks: {
            type: Object,
            default: () => ({ prev: null, next: null }),
        },
        isAuthenticated: {
            type: Boolean,
            default: false,
        },
        createUrl: {
            type: String,
            default: '/libraries/create',
        },
    },
    data() {
        return {
            libraries: this.initialLibraries || [],
            meta: Object.assign({ current_page: 1, last_page: 1, per_page: 12, total: 0 }, this.initialMeta || {}),
            links: Object.assign({ prev: null, next: null }, this.initialLinks || {}),
            filters: {
                search: this.initialFilters.search || '',
                sort: this.initialFilters.sort === 'name' ? 'name_asc' : (this.initialFilters.sort || 'popular'),
            },
            activeMode: this.initialFilters.mode || 'all',
            visibility: this.initialFilters.visibility || 'all',
            ownerUsername: this.initialFilters.user || '',
            visibilityOptions: [
                { value: 'all', label: 'Усі' },
                { value: 'public', label: 'Публічні' },
                { value: 'private', label: 'Приватні' },
            ],
            managedLibrary: null,
            loading: false,
            error: null,
            searchTimer: null,
            librariesRequest: 0,
        };
    },
    computed: {
        hasLibraries() {
            return Array.isArray(this.libraries) && this.libraries.length > 0;
        },
        emptyTitle() {
            if (this.activeMode === 'saved') return 'Збережених добірок немає';
            if (this.activeMode === 'my') return 'Ваших добірок не знайдено';
            return 'Добірки не знайдено';
        },
        emptyMessage() {
            if (this.activeMode === 'saved') return 'Збережіть публічну добірку, щоб вона з’явилася тут.';
            if (this.activeMode === 'my') return 'Змініть фільтри або створіть першу добірку.';
            return 'Спробуйте змінити пошук або сортування.';
        },
        hasNonDefaultFilters() {
            return Boolean(
                this.filters.search
                || this.filters.sort !== 'popular'
                || (this.activeMode === 'my' && this.visibility !== 'all')
                || (this.activeMode === 'all' && this.ownerUsername)
            );
        },
        pages() {
            if (!this.meta || !this.meta.last_page) {
                return [];
            }
            const total = this.meta.last_page;
            const current = this.meta.current_page || 1;
            const delta = 2;
            let start = Math.max(1, current - delta);
            let end = Math.min(total, current + delta);

            while (end - start < 4 && start > 1) {
                start--;
            }
            while (end - start < 4 && end < total) {
                end++;
            }

            const list = [];
            for (let i = start; i <= end; i += 1) {
                list.push(i);
            }
            return list;
        },
    },
    methods: {
        async fetchLibraries(page = 1, { background = false } = {}) {
            const requestId = ++this.librariesRequest;
            this.loading = !background;
            if (!background) this.error = null;
            try {
                const response = await axios.get('/libraries', {
                    params: {
                        search: this.filters.search || undefined,
                        sort: this.filters.sort || undefined,
                        mode: this.activeMode,
                        visibility: this.activeMode === 'my' ? this.visibility : undefined,
                        user: this.activeMode === 'all' ? (this.ownerUsername || undefined) : undefined,
                        page,
                    },
                    headers: {
                        Accept: 'application/json',
                    },
                });
                if (requestId !== this.librariesRequest) return false;
                const data = response.data || {};
                this.libraries = data.data || [];
                this.meta = Object.assign({ current_page: page, last_page: 1, per_page: 12, total: 0 }, data.meta || {});
                this.links = Object.assign({ prev: null, next: null }, data.links || {});
                this.error = null;
                this.updateUrl();
                return true;
            } catch (error) {
                if (requestId !== this.librariesRequest) return false;
                console.error('Failed to fetch libraries', error);
                const message = background ? 'Не вдалося оновити прев’ю добірки.' : 'Не вдалося завантажити добірки.';
                if (!background) this.error = message;
                this.showNotification(message, 'error');
                return false;
            } finally {
                if (requestId === this.librariesRequest) this.loading = false;
            }
        },
        handleSearchInput() {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => {
                this.fetchLibraries(1);
            }, 400);
        },
        clearSearch() {
            clearTimeout(this.searchTimer);
            this.filters.search = '';
            this.fetchLibraries(1);
        },
        applyFilters() {
            clearTimeout(this.searchTimer);
            this.fetchLibraries(1);
        },
        switchMode(mode) {
            if (!this.isAuthenticated && mode !== 'all') return;
            if (this.activeMode === mode) return;
            this.activeMode = mode;
            this.ownerUsername = '';
            this.managedLibrary = null;
            this.fetchLibraries(1);
        },
        setVisibility(visibility) {
            if (this.visibility === visibility) return;
            this.visibility = visibility;
            this.fetchLibraries(1);
        },
        resetFilters() {
            clearTimeout(this.searchTimer);
            this.filters.search = '';
            this.filters.sort = 'popular';
            this.visibility = 'all';
            this.ownerUsername = '';
            this.fetchLibraries(1);
        },
        goToPage(page) {
            if (!this.meta) {
                return;
            }
            const total = this.meta.last_page || 1;
            if (page < 1 || page > total) {
                return;
            }
            this.fetchLibraries(page);
        },
        updateUrl() {
            if (typeof window === 'undefined' || !window.history) {
                return;
            }
            const params = new URLSearchParams();
            if (this.filters.search) {
                params.set('search', this.filters.search);
            }
            if (this.filters.sort && this.filters.sort !== 'popular') {
                params.set('sort', this.filters.sort);
            }
            if (this.activeMode !== 'all') {
                params.set('mode', this.activeMode);
            }
            if (this.activeMode === 'my' && this.visibility !== 'all') {
                params.set('visibility', this.visibility);
            }
            if (this.activeMode === 'all' && this.ownerUsername) {
                params.set('user', this.ownerUsername);
            }
            if (this.meta && this.meta.current_page && this.meta.current_page > 1) {
                params.set('page', this.meta.current_page);
            }
            const query = params.toString();
            const url = query ? `${window.location.pathname}?${query}` : window.location.pathname;
            window.history.replaceState({}, '', url);
        },
        goToCreate() {
            window.location.href = this.createUrl;
        },
        handleLiked({ libraryId, isLiked, likesCount }) {
            const library = this.libraries.find((item) => item.id === libraryId);
            if (library) {
                this.$set(library, 'is_liked', isLiked);
                this.$set(library, 'likes_count', likesCount);
            }
        },
        handleSaved({ libraryId, isSaved }) {
            const library = this.libraries.find((item) => item.id === libraryId);
            if (library) {
                this.$set(library, 'is_saved', isSaved);
            }
            if (this.activeMode === 'saved' && !isSaved) {
                this.libraries = this.libraries.filter((item) => item.id !== libraryId);
                this.meta.total = Math.max(0, this.meta.total - 1);
            }
        },
        openManager(library) {
            this.managedLibrary = library;
        },
        async handleBooksChanged({ libraryId, booksCount, mutationType, bookId }) {
            const library = this.libraries.find((item) => Number(item.id) === Number(libraryId));
            if (library) {
                this.$set(library, 'books_count', booksCount);
                if (mutationType === 'remove' && Array.isArray(library.books)) {
                    this.$set(library, 'books', library.books.filter((book) => Number(book.id) !== Number(bookId)));
                }
            }
            await this.fetchLibraries(this.meta.current_page, { background: true });
        },
        async deleteLibrary(library) {
            const confirmed = await window.confirm(`Видалити добірку «${library.name}»? Дію неможливо скасувати.`);
            if (!confirmed) return;
            try {
                const response = await axios.delete(`/libraries/${library.slug}`, {
                    headers: { Accept: 'application/json' },
                });
                this.libraries = this.libraries.filter((item) => item.id !== library.id);
                this.meta.total = Math.max(0, this.meta.total - 1);
                this.showNotification(response.data?.message || 'Добірку видалено успішно!');
            } catch (error) {
                this.showNotification(error.response?.data?.message || 'Не вдалося видалити добірку.', 'error');
            }
        },
        showNotification(message, type = 'success') {
            const existing = document.querySelectorAll('.notification');
            existing.forEach((node) => node.remove());

            const notification = document.createElement('div');
            notification.className = `notification fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg transition-all duration-300 transform translate-x-full ${type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`;
            notification.textContent = message;

            document.body.appendChild(notification);

            setTimeout(() => {
                notification.style.transform = 'translateX(0)';
            }, 100);

            setTimeout(() => {
                notification.style.transform = 'translateX(100%)';
                setTimeout(() => notification.remove(), 300);
            }, 3000);
        },
    },
    beforeDestroy() {
        clearTimeout(this.searchTimer);
        this.librariesRequest += 1;
        this.loading = false;
    },
    mounted() {
        this.updateUrl();
    },
};
</script>
